import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import PublicHeader from "../components/Layout/PublicHeader";
import Footer from "../components/Layout/Footer";
import HeroVideo from "../components/Layout/HeroVideo";
import { authService } from "../services/authService";
import "../styles/homepage.css";

const HomePage = () => {
  const history = useHistory();
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [isHowItWorksVisible, setIsHowItWorksVisible] = useState(false);
  const statsRef = useRef(null);
  const howItWorksRef = useRef(null);

  const handlePasserLivraison = () => {
    if (authService.isAuthenticated()) {
      history.push("/commande");
    } else {
      history.push("/login");
    }
  };

  // Intersection Observer pour détecter quand la section stats est visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isStatsVisible) {
          setIsStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [isStatsVisible]);

  // Intersection Observer pour détecter quand la section "How it works" est visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isHowItWorksVisible) {
          setIsHowItWorksVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (howItWorksRef.current) {
      observer.observe(howItWorksRef.current);
    }

    return () => {
      if (howItWorksRef.current) {
        observer.unobserve(howItWorksRef.current);
      }
    };
  }, [isHowItWorksVisible]);

  // Composant de compteur animé
  const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isStatsVisible) return;

      let startTime;
      let animationFrame;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeOutQuart * end));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }, [isStatsVisible, end, duration]);

    return <span>{count}{suffix}</span>;
  };

  return (
    <div className="homepage">
      <PublicHeader />
      <HeroVideo />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col md={6}>
              <h1 className="display-4 fw-bold mb-4">
                Ta livraison. Maintenant. Partout.
              </h1>
              <p className="lead mb-4">
                Nous livrons tes matériaux, colis, achats magasin et équipements - pour particuliers, artisans et professionnels du BTP, rapidement et en direct depuis tes fournisseurs, dépôts ou magasins.
              </p>
              <div className="d-flex gap-3">
                <Button
                  variant="primary"
                  size="lg"
                  className="btn-marion"
                  onClick={handlePasserLivraison}
                >
                  Commander une livraison
                </Button>
                <Button
                  variant="outline-primary"
                  size="lg"
                  className="btn-outline-marion"
                  onClick={() => history.push("/register")}
                >
                  Créer un compte
                </Button>
              </div>
            </Col>
            <Col md={6}>
              <div className="hero-image-placeholder">
                <img
                  src="/images/banner-hero.jpg"
                  alt="Livraison Marion"
                  className="img-fluid rounded shadow"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.innerHTML =
                      '<div class="placeholder-banner">Image bandeau à venir</div>';
                  }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section py-5" ref={statsRef}>
        <Container>
          <Row className="text-center">
            <Col md={4} className="mb-4 mb-md-0">
              <div className={`stat-card ${isStatsVisible ? 'stat-card-animated' : ''}`} style={{ animationDelay: '0s' }}>
                <div className="stat-icon mb-3">
                  <i className="fas fa-truck fa-3x text-primary"></i>
                </div>
                <h3 className="stat-number fw-bold">
                  + <AnimatedCounter end={3000} />
                </h3>
                <p className="stat-label text-muted">livraisons réalisées</p>
              </div>
            </Col>
            <Col md={4} className="mb-4 mb-md-0">
              <div className={`stat-card ${isStatsVisible ? 'stat-card-animated' : ''}`} style={{ animationDelay: '0.2s' }}>
                <div className="stat-icon mb-3">
                  <i className="fas fa-users fa-3x text-primary"></i>
                </div>
                <h3 className="stat-number fw-bold">
                  + <AnimatedCounter end={45} /> entreprises & + <AnimatedCounter end={300} /> particuliers
                </h3>
                <p className="stat-label text-muted">du BTP servis</p>
              </div>
            </Col>
            <Col md={4} className="mb-4 mb-md-0">
              <div className={`stat-card ${isStatsVisible ? 'stat-card-animated' : ''}`} style={{ animationDelay: '0.4s' }}>
                <div className="stat-icon mb-3">
                  <i className="fas fa-dumbbell fa-3x text-primary"></i>
                </div>
                <h3 className="stat-number fw-bold">
                  + <AnimatedCounter end={400} /> tonnes
                </h3>
                <p className="stat-label text-muted">livrées</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section py-5 bg-light">
        <Container>
          <h2 className="text-center mb-5">Pourquoi choisir Marion Livraison ?</h2>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Row>
                <Col md={6} className="mb-4">
                  <div className="feature-item d-flex align-items-start h-100">
                    <div className="feature-icon-small me-3">
                      <i className="fas fa-hard-hat fa-2x text-primary"></i>
                    </div>
                    <div>
                      <h5 className="mb-2">Pour les pros du BTP</h5>
                      <p className="text-muted mb-0">
                        Urgences chantier, matériaux, palettes, outillage, équipements spécifiques
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="feature-item d-flex align-items-start h-100">
                    <div className="feature-icon-small me-3">
                      <i className="fas fa-home fa-2x text-primary"></i>
                    </div>
                    <div>
                      <h5 className="mb-2">Pour les particuliers</h5>
                      <p className="text-muted mb-0">
                        Achats volumineux, meubles, colis lourds, matériaux de rénovation
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="feature-item d-flex align-items-start h-100">
                    <div className="feature-icon-small me-3">
                      <i className="fas fa-user-tie fa-2x text-primary"></i>
                    </div>
                    <div>
                      <h5 className="mb-2">Chauffeurs pros & véhicules adaptés</h5>
                      <p className="text-muted mb-0">
                        Sanglage, protections, manutention
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="feature-item d-flex align-items-start h-100">
                    <div className="feature-icon-small me-3">
                      <i className="fas fa-shipping-fast fa-2x text-primary"></i>
                    </div>
                    <div>
                      <h5 className="mb-2">Livraison directe sans groupage</h5>
                      <p className="text-muted mb-0">
                        Ton chargement livré en direct, sans passage par un hub
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="feature-item d-flex align-items-start h-100">
                    <div className="feature-icon-small me-3">
                      <i className="fas fa-euro-sign fa-2x text-primary"></i>
                    </div>
                    <div>
                      <h5 className="mb-2">Tarifs dynamiques et transparents</h5>
                      <p className="text-muted mb-0">
                        Dès 19,99 €
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How it works Section */}
      <section className="how-it-works-section py-5" ref={howItWorksRef}>
        <Container>
          <h2 className="text-center mb-5">Comment ça marche ?</h2>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className={`step-item ${isHowItWorksVisible ? 'step-item-animated' : ''}`} style={{ animationDelay: '0s' }}>
                <div className="step-number mb-3">1</div>
                <h5>Commande en 1 minute</h5>
                <p className="text-muted">
                  Simple et rapide, directement en ligne
                </p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className={`step-item ${isHowItWorksVisible ? 'step-item-animated' : ''}`} style={{ animationDelay: '0.2s' }}>
                <div className="step-number mb-3">2</div>
                <h5>On récupère ta marchandise</h5>
                <p className="text-muted">
                  Magasin, dépôt, fournisseur, particulier…
                </p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className={`step-item ${isHowItWorksVisible ? 'step-item-animated' : ''}`} style={{ animationDelay: '0.4s' }}>
                <div className="step-number mb-3">3</div>
                <h5>Livraison directe et rapide</h5>
                <p className="text-muted">
                  Sans détour, droit vers ta destination
                </p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className={`step-item ${isHowItWorksVisible ? 'step-item-animated' : ''}`} style={{ animationDelay: '0.6s' }}>
                <div className="step-number mb-3">4</div>
                <h5>Confirmation + preuve</h5>
                <p className="text-muted">
                  Preuve de livraison instantanée
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col>
              <h2 className="mb-4">Prêt à recevoir ta 1ere commande ?</h2>
              <Button
                variant="light"
                size="lg"
                onClick={handlePasserLivraison}
              >
                Commencer maintenant
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
